import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../context/AuthContext';
import PayInvoiceModal, { PayInvoiceForm } from '../components/PayInvoiceModal';
import { dueService, invoiceService, roomService, tenantService } from '../services/api';
import { Due, Invoice, Room, User } from '../types';
import { toast } from 'react-toastify';
import {
  normalizePaymentType,
  paymentTypeLabel,
  recordMatchesSearch,
  recordRoomLabel,
} from '../utils/paymentDisplay';
import PhoneLink from '../components/PhoneLink';
import TenantNameLink from '../components/TenantNameLink';
import './Invoices.css';
import './DuesManagement.css';

type DueStatusFilter = 'OVERDUE' | 'UPCOMING' | 'CURRENT' | 'ALL';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function hasOutstandingBalance(due: Due): boolean {
  return Number(due.balanceAmount || 0) > 0.01;
}

const DuesManagement: React.FC = () => {
  const { hostel } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dues, setDues] = useState<Due[]>([]);
  const [layoutRooms, setLayoutRooms] = useState<Room[]>([]);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<DueStatusFilter>('ALL');
  const [filter, setFilter] = useState({ month: '', year: '', type: '' });
  const [payTenant, setPayTenant] = useState<User | null>(null);
  const [payDueId, setPayDueId] = useState<number | null>(null);
  const [paySourceInvoiceId, setPaySourceInvoiceId] = useState<number | null>(null);
  const [paySettleSummary, setPaySettleSummary] = useState<{
    month: string;
    year: string;
    amount: number;
  } | null>(null);
  const [payInitialForm, setPayInitialForm] = useState<Partial<PayInvoiceForm> | null>(null);
  const [payLoading, setPayLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const roomById = useMemo(
    () => Object.fromEntries(layoutRooms.map((r) => [r.id, r])) as Record<number, Room>,
    [layoutRooms]
  );

  const load = useCallback(async () => {
    if (!hostel?.id) return;
    try {
      setLoading(true);
      const data = await dueService.getDuesByHostel(hostel.id, { status });
      setDues(data.filter(hasOutstandingBalance));
    } catch {
      toast.error('Failed to load dues');
      setDues([]);
    } finally {
      setLoading(false);
    }
  }, [hostel?.id, status]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!hostel?.id) {
      setLayoutRooms([]);
      return;
    }
    roomService
      .getRoomsByHostel(hostel.id)
      .then((rooms) => setLayoutRooms(rooms.filter((r) => r.isActive !== false)))
      .catch(() => setLayoutRooms([]));
  }, [hostel?.id]);

  const filteredDues = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = dues.filter((d) => {
      if (!hasOutstandingBalance(d)) return false;
      if (filter.month && String(d.paymentMonth ?? '').trim().toLowerCase() !== filter.month.trim().toLowerCase()) {
        return false;
      }
      if (filter.year && String(d.paymentYear ?? '').trim() !== filter.year.trim()) return false;
      if (filter.type && normalizePaymentType(d.paymentType) !== normalizePaymentType(filter.type)) {
        return false;
      }
      if (q) {
        const roomLabel = recordRoomLabel(d.room, roomById);
        if (!recordMatchesSearch(q, { name: d.name, phone: d.phoneNumber, room: roomLabel })) {
          return false;
        }
      }
      return true;
    });
    return [...list].sort((a, b) => {
      const da = a.dueDate ? new Date(a.dueDate).getTime() : NaN;
      const db = b.dueDate ? new Date(b.dueDate).getTime() : NaN;
      const aDue = Number.isFinite(da) ? da : 0;
      const bDue = Number.isFinite(db) ? db : 0;
      if (bDue !== aDue) return bDue - aDue;
      const ta = a.paymentTimestamp ? new Date(a.paymentTimestamp).getTime() : NaN;
      const tb = b.paymentTimestamp ? new Date(b.paymentTimestamp).getTime() : NaN;
      const aTime = Number.isFinite(ta) ? ta : 0;
      const bTime = Number.isFinite(tb) ? tb : 0;
      if (bTime !== aTime) return bTime - aTime;
      return b.id - a.id;
    });
  }, [dues, filter, query, roomById]);

  useEffect(() => {
    setPage(1);
  }, [query, status, filter.month, filter.year, filter.type, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredDues.length / pageSize));
  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredDues.slice(start, start + pageSize);
  }, [filteredDues, page, pageSize]);

  const pageNumbers = useMemo(() => {
    const max = Math.min(totalPages, 10);
    const nums: number[] = [];
    for (let i = 1; i <= max; i++) nums.push(i);
    return nums;
  }, [totalPages]);

  const exportSlug = () => (hostel?.name || 'dues').replace(/[^\w\-]+/g, '_').slice(0, 48);

  const handleExportPdf = () => {
    if (!hostel) return;
    if (filteredDues.length === 0) {
      toast.error('No dues to export.');
      return;
    }
    try {
      const head = ['Due ID', 'Name', 'Room', 'Phone', 'Month/Year', 'Type', 'Balance', 'Due date', 'Status'];
      const body = filteredDues.map((d) => [
        String(d.id),
        d.name || '',
        recordRoomLabel(d.room, roomById) || '—',
        d.phoneNumber || '',
        `${d.paymentMonth}/${d.paymentYear}`,
        paymentTypeLabel(d.paymentType),
        `₹${Number(d.balanceAmount || 0).toLocaleString('en-IN')}`,
        d.dueDate ? new Date(d.dueDate).toLocaleDateString() : '—',
        d.dueStatus || 'CURRENT',
      ]);
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      doc.setFontSize(11);
      doc.setTextColor(55, 71, 79);
      doc.text(`${hostel.name} — Dues`, 40, 28);
      autoTable(doc, {
        startY: 38,
        head: [head],
        body,
        styles: { fontSize: 7, cellPadding: 2.5, textColor: [55, 71, 79] },
        headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { left: 28, right: 28 },
      });
      doc.save(`${exportSlug()}-dues.pdf`);
      toast.success('PDF downloaded');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Could not create PDF.');
    }
  };

  const closePayModal = () => {
    setPayTenant(null);
    setPayDueId(null);
    setPaySourceInvoiceId(null);
    setPaySettleSummary(null);
    setPayInitialForm(null);
  };

  const handlePay = async (due: Due) => {
    if (!hostel?.id) return;
    setPayLoading(true);
    try {
      let user: User;
      try {
        user = await tenantService.getHostelTenant(hostel.id, due.userId);
      } catch {
        user = {
          id: due.userId,
          username: due.name,
          phoneNumber: due.phoneNumber,
          floor: due.floor,
          room: due.room,
          bed: due.bed,
        } as User;
      }
      const balance = Math.round(Number(due.balanceAmount) || 0);
      let invoiceId = due.invoiceId ?? null;
      if (!invoiceId) {
        const invs = await invoiceService.getUserInvoices(due.userId);
        const match = invs
          .filter(
            (inv) =>
              inv.paymentMonth === due.paymentMonth &&
              String(inv.paymentYear) === String(due.paymentYear) &&
              Number(inv.balanceAmount || 0) > 0
          )
          .sort((a, b) => Number(b.id) - Number(a.id))[0];
        invoiceId = match?.id ?? null;
      }
      if (!invoiceId) {
        toast.error('No invoice with balance found for this due. Create an invoice with balance first.');
        return;
      }
      setPayDueId(due.id);
      setPaySourceInvoiceId(invoiceId);
      setPaySettleSummary({
        month: due.paymentMonth,
        year: String(due.paymentYear),
        amount: balance,
      });
      setPayInitialForm({
        paymentMonth: due.paymentMonth,
        paymentYear: String(due.paymentYear),
        paymentType: normalizePaymentType(due.paymentType) as Invoice['paymentType'],
        paidAmount: balance > 0 ? String(balance) : '',
        balanceAmount: '0',
        discount: '',
        dueDate: '',
      });
      setPayTenant(user);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      toast.error(e?.response?.data?.message || 'Could not open payment for this tenant');
    } finally {
      setPayLoading(false);
    }
  };

  if (!hostel) return <div className="loading">Loading...</div>;

  return (
    <div className="invoices-page">
      <header className="invoices-page-header">
        <h1>Dues</h1>
      </header>

      <div className="invoices-toolbar">
        <label className="invoices-search-field">
          <span className="invoices-search-icon" aria-hidden>
            🔍
          </span>
          <input
            type="search"
            className="invoices-search-input"
            placeholder="Search by name or room…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search dues"
          />
        </label>
        <div className="filters">
          <select value={status} onChange={(e) => setStatus(e.target.value as DueStatusFilter)} aria-label="Due status">
            <option value="ALL">All statuses</option>
            <option value="CURRENT">Current</option>
            <option value="UPCOMING">Upcoming</option>
            <option value="OVERDUE">Overdue</option>
          </select>
          <select
            value={filter.month}
            onChange={(e) => setFilter({ ...filter, month: e.target.value })}
            aria-label="Month"
          >
            <option value="">All Months</option>
            {MONTHS.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          <select
            value={filter.year}
            onChange={(e) => setFilter({ ...filter, year: e.target.value })}
            aria-label="Year"
          >
            <option value="">All Years</option>
            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
              <option key={y} value={y.toString()}>{y}</option>
            ))}
          </select>
          <select
            value={filter.type}
            onChange={(e) => setFilter({ ...filter, type: e.target.value })}
            aria-label="Payment type"
          >
            <option value="">All Types</option>
            <option value="RENT">Rent</option>
            <option value="ADVANCE">Advance</option>
            <option value="REFUND">Refund</option>
          </select>
        </div>
      </div>

      <div className="dues-actions-row">
        <button type="button" className="dues-action-btn dues-action-btn--export" onClick={handleExportPdf} disabled={loading}>
          Export PDF
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading dues...</div>
      ) : (
        <div className="invoices-table-container responsive-table-wrap">
          <table className="invoices-table responsive-table">
            <thead>
              <tr>
                <th>Due ID</th>
                <th>Name</th>
                <th>Room</th>
                <th>Phone</th>
                <th>Month/Year</th>
                <th>Type</th>
                <th>Paid Amount</th>
                <th>Discount</th>
                <th>Balance</th>
                <th>Due date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.length === 0 ? (
                <tr className="responsive-table__empty">
                  <td colSpan={12} className="no-data" data-label="">
                    No outstanding dues found
                  </td>
                </tr>
              ) : (
                pageItems.map((due) => (
                  <tr key={due.id}>
                    <td data-label="Due">#{due.id}</td>
                    <td className="responsive-table__primary" data-label="Name">
                      <TenantNameLink userId={due.userId} name={due.name} />
                    </td>
                    <td data-label="Room">{recordRoomLabel(due.room, roomById) || '—'}</td>
                    <td data-label="Phone">
                      <PhoneLink phone={due.phoneNumber} />
                    </td>
                    <td data-label="Month/Year">
                      {due.paymentMonth}/{due.paymentYear}
                    </td>
                    <td data-label="Type">
                      <span className={`type-badge ${normalizePaymentType(due.paymentType).toLowerCase()}`}>
                        {paymentTypeLabel(due.paymentType)}
                      </span>
                    </td>
                    <td data-label="Paid">
                      ₹{Number(due.paidAmount || 0).toLocaleString()}
                    </td>
                    <td data-label="Discount">
                      ₹{Number(due.discount || 0).toLocaleString()}
                    </td>
                    <td className="balance-due" data-label="Balance">
                      ₹{Number(due.balanceAmount || 0).toLocaleString()}
                    </td>
                    <td data-label="Due date">
                      {due.dueDate ? new Date(due.dueDate).toLocaleDateString() : '—'}
                    </td>
                    <td data-label="Status">
                      <span className={`status-badge ${(due.dueStatus || 'current').toLowerCase()}`}>
                        {due.dueStatus || 'CURRENT'}
                      </span>
                    </td>
                    <td className="responsive-table__actions" data-label="Actions">
                      <button
                        type="button"
                        className="pay-btn"
                        disabled={payLoading}
                        onClick={() => void handlePay(due)}
                      >
                        Pay
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <footer className="invoices-table-footer">
            <div className="invoices-pagination">
              <button
                type="button"
                className="invoices-page-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                ←
              </button>
              {pageNumbers.map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`invoices-page-btn${n === page ? ' is-active' : ''}`}
                  onClick={() => setPage(n)}
                >
                  {n}
                </button>
              ))}
              <button
                type="button"
                className="invoices-page-btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label="Next page"
              >
                →
              </button>
            </div>
            <div className="invoices-footer-meta">
              <span>
                Showing {filteredDues.length === 0 ? 0 : (page - 1) * pageSize + 1} to{' '}
                {Math.min(page * pageSize, filteredDues.length)} of {filteredDues.length} entries
              </span>
              <label className="invoices-page-size">
                Show
                <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} aria-label="Rows per page">
                  <option value={8}>8</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </label>
            </div>
          </footer>
        </div>
      )}

      <PayInvoiceModal
        tenant={payTenant}
        dueId={payDueId}
        sourceInvoiceId={paySourceInvoiceId}
        settleDueSummary={paySettleSummary}
        initialForm={payInitialForm}
        onClose={closePayModal}
        onSuccess={() => load()}
      />

    </div>
  );
};

export default DuesManagement;
